import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        params: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "flexible";
          callback?: (token: string) => void;
          "error-callback"?: (code?: unknown) => void;
          "expired-callback"?: () => void;
          "timeout-callback"?: () => void;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

export interface TurnstileWidgetRef {
  reset: () => void;
}

interface TurnstileWidgetProps {
  onSuccess: (token: string) => void;
  onError?: (err?: unknown) => void;
  onExpire?: () => void;
  siteKey?: string;
}

const DEFAULT_TEST_SITE_KEY = "1x00000000000000000000AA";

export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  ({ onSuccess, onError, onExpire, siteKey }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);

    const rawSiteKey = siteKey || import.meta.env.VITE_TURNSTILE_SITE_KEY;
    const isProductionHost =
      typeof window !== "undefined" &&
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1";

    const isTestKey =
      !rawSiteKey ||
      rawSiteKey === DEFAULT_TEST_SITE_KEY ||
      rawSiteKey === "1x00000000000000000000AA";

    const isDisabled =
      rawSiteKey === "disabled" ||
      rawSiteKey === "none" ||
      rawSiteKey === "false" ||
      (isProductionHost && isTestKey);

    const activeSiteKey = rawSiteKey || DEFAULT_TEST_SITE_KEY;

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (isDisabled) {
          onSuccess("bypassed");
          return;
        }
        if (window.turnstile && widgetIdRef.current) {
          try {
            window.turnstile.reset(widgetIdRef.current);
          } catch (e) {
            console.error("Turnstile reset error:", e);
          }
        }
      },
    }));

    useEffect(() => {
      if (isDisabled) {
        onSuccess("bypassed");
        return;
      }
      let isMounted = true;

      const renderWidget = () => {
        if (!containerRef.current || !window.turnstile || !isMounted) return;

        // If a widget was previously rendered in this ref, remove it first
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // ignore
          }
          widgetIdRef.current = null;
        }

        try {
          const id = window.turnstile.render(containerRef.current, {
            sitekey: activeSiteKey,
            theme: "dark",
            size: "normal",
            callback: (token: string) => {
              if (isMounted) onSuccess(token);
            },
            "error-callback": (err: unknown) => {
              if (isMounted && onError) onError(err);
            },
            "expired-callback": () => {
              if (isMounted && onExpire) onExpire();
            },
            "timeout-callback": () => {
              if (isMounted && onError) onError("timeout");
            },
          });
          widgetIdRef.current = id;
        } catch (e) {
          console.error("Failed to render Cloudflare Turnstile:", e);
          if (onError) onError(e);
        }
      };

      if (window.turnstile) {
        renderWidget();
      } else {
        const existingScript = document.getElementById("cf-turnstile-script");
        if (!existingScript) {
          const script = document.createElement("script");
          script.id = "cf-turnstile-script";
          script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
          script.async = true;
          script.defer = true;
          script.onload = () => {
            renderWidget();
          };
          document.head.appendChild(script);
        } else {
          // If script already exists but window.turnstile not ready yet
          const interval = setInterval(() => {
            if (window.turnstile) {
              clearInterval(interval);
              renderWidget();
            }
          }, 100);
          return () => clearInterval(interval);
        }
      }

      return () => {
        isMounted = false;
        if (window.turnstile && widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // ignore
          }
          widgetIdRef.current = null;
        }
      };
    }, [activeSiteKey, isDisabled, onSuccess, onError, onExpire]);

    if (isDisabled) {
      return null;
    }

    return (
      <div className="tx-turnstile-wrapper" aria-label="Bot Protection Verification">
        <div ref={containerRef} />
      </div>
    );
  }
);

TurnstileWidget.displayName = "TurnstileWidget";
export default TurnstileWidget;
